import { useState } from 'react';
import {
  saveActivityDraftAsync,
  submitActivityEntryAsync,
  saveDistrictActivityDraftAsync,
  submitDistrictActivityEntryAsync,
  saveNationActivityDraftAsync,
  submitNationActivityEntryAsync,
} from '@features/report/activity-entry/api/activityEntryApi';
import {
  SaveActivityEntryDraftRequest,
  SubmitActivityEntryRequest,
} from '@features/report/activity-entry/types/localReportSubmissionRequests';
import { showSuccess } from '@infrastructure/api/utils/toastNotifications';
import { useApiError } from '@infrastructure/api/hooks/useApiError';
import { ErrorResponse } from '@core/types/errorResponse';
import { ApiMessageResponse } from '@core/types/apiMessageResponse';
import ApiResponse from '@core/types/apiResponse';

type SaveDraftOptions = {
  silent?: boolean;
};

export type ActivityEntryScope = 'church' | 'district' | 'nation';

function missingScopeResponse(): ApiResponse<ApiMessageResponse> {
  const error: ErrorResponse = {
    statusCode: 400,
    title: 'Activity entry scope required',
    detail: 'Cannot save or submit without a workspace scope.',
  };
  return {
    data: null,
    error,
    status: 400,
    isSuccess: () => false,
    isError: () => true,
    getData: () => null,
    getError: () => error,
  };
}

export function useActivityEntryAction(scope?: ActivityEntryScope) {
  const [busy, setBusy] = useState(false);
  const showError = useApiError();

  const doSaveDraft = async (
    entityId: number,
    data: SaveActivityEntryDraftRequest,
    options: SaveDraftOptions = {}
  ): Promise<ApiResponse<ApiMessageResponse>> => {
    if (!scope) {
      return missingScopeResponse();
    }

    setBusy(true);
    try {
      const response =
        scope === 'district'
          ? await saveDistrictActivityDraftAsync(entityId, data)
          : scope === 'nation'
            ? await saveNationActivityDraftAsync(entityId, data)
            : await saveActivityDraftAsync(entityId, data);

      if (response.isSuccess()) {
        if (!options.silent) {
          showSuccess(response.getData()?.message ?? 'Draft saved successfully.');
        }
      } else {
        showError(response.getError() as ErrorResponse);
      }
      return response;
    } catch (error) {
      showError(error as ErrorResponse);
      throw error;
    } finally {
      setBusy(false);
    }
  };

  const doSubmit = async (
    entityId: number,
    data: SubmitActivityEntryRequest
  ): Promise<ApiResponse<ApiMessageResponse>> => {
    if (!scope) {
      return missingScopeResponse();
    }

    setBusy(true);
    try {
      const response =
        scope === 'district'
          ? await submitDistrictActivityEntryAsync(entityId, data)
          : scope === 'nation'
            ? await submitNationActivityEntryAsync(entityId, data)
            : await submitActivityEntryAsync(entityId, data);

      if (response.isSuccess()) {
        showSuccess(response.getData()?.message ?? 'Activity entries submitted successfully.');
      } else {
        showError(response.getError() as ErrorResponse);
      }
      return response;
    } catch (error) {
      showError(error as ErrorResponse);
      throw error;
    } finally {
      setBusy(false);
    }
  };

  return { busy, doSaveDraft, doSubmit };
}
